import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ticketsApi, branchesApi, claimsApi, patientsApi, payersApi } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { ArrowLeft, FloppyDisk, Ticket } from '@phosphor-icons/react';
import { Toaster, toast } from 'sonner';

const NewTicketPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [branches, setBranches] = useState([]);
  const [users, setUsers] = useState([]);
  const [claimSearch, setClaimSearch] = useState('');
  const [claims, setClaims] = useState([]);
  const [patientSearch, setPatientSearch] = useState('');
  const [patients, setPatients] = useState([]);
  const [payers, setPayers] = useState([]);

  const [form, setForm] = useState({
    category: '', sub_category: '', priority: 'medium', subject: '', description: '',
    branch_id: '', department: '', related_claim_id: '', related_patient_id: '',
    related_payer_id: '', assigned_to: '',
  });

  useEffect(() => {
    const load = async () => {
      try {
        const [catRes, brRes, usRes, payRes] = await Promise.all([
          ticketsApi.categories(), branchesApi.list(), ticketsApi.assignableUsers(), payersApi.listActive(),
        ]);
        setCategories(catRes.data.categories || []);
        setBranches(brRes.data || []);
        setUsers(usRes.data || []);
        setPayers(payRes.data || []);
      } catch (e) { console.error(e); }
    };
    load();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      if (claimSearch && claimSearch.length >= 2) {
        claimsApi.list({ search: claimSearch, limit: 10 }).then(r => setClaims(r.data?.claims || r.data || [])).catch(() => {});
      }
    }, 300);
    return () => clearTimeout(t);
  }, [claimSearch]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (patientSearch && patientSearch.length >= 2) {
        patientsApi.list(patientSearch).then(r => setPatients(r.data || [])).catch(() => {});
      }
    }, 300);
    return () => clearTimeout(t);
  }, [patientSearch]);

  const handleChange = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.category || !form.subject || !form.description) {
      toast.error('Category, subject, and description are required');
      return;
    }
    setLoading(true);
    try {
      const res = await ticketsApi.create(form);
      toast.success(`Ticket ${res.data.ticket_id} created`);
      navigate('/tickets');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create ticket');
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-5" data-testid="new-ticket-page">
      <Toaster position="top-right" richColors />

      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/tickets')} className="text-slate-500" data-testid="back-btn">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back
        </Button>
        <div className="flex items-center gap-2">
          <Ticket className="h-5 w-5 text-indigo-500" weight="duotone" />
          <h1 className="text-2xl font-bold font-['Outfit'] text-slate-800" data-testid="new-ticket-heading">Raise New Ticket</h1>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

          {/* Ticket Details */}
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold font-['Outfit'] text-slate-800">Ticket Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Category *</Label>
                  <Select value={form.category} onValueChange={v => handleChange('category', v)} required>
                    <SelectTrigger className="border-slate-200" data-testid="category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                    <SelectContent>
                      {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Priority *</Label>
                  <Select value={form.priority} onValueChange={v => handleChange('priority', v)}>
                    <SelectTrigger className="border-slate-200" data-testid="priority-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Sub-Category</Label>
                <Input placeholder="Optional sub-category" value={form.sub_category}
                  onChange={e => handleChange('sub_category', e.target.value)} className="border-slate-200" data-testid="subcategory-input" />
              </div>

              <div>
                <Label>Subject *</Label>
                <Input placeholder="Brief summary of the issue" value={form.subject}
                  onChange={e => handleChange('subject', e.target.value)} className="border-slate-200" data-testid="subject-input" required />
              </div>

              <div>
                <Label>Description *</Label>
                <Textarea placeholder="Describe the issue in detail..." rows={5} value={form.description}
                  onChange={e => handleChange('description', e.target.value)} className="border-slate-200 resize-none" data-testid="description-input" required />
              </div>
            </CardContent>
          </Card>

          {/* Assignment & Context */}
          <div className="space-y-5">
            <Card className="bg-white border border-slate-200 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold font-['Outfit'] text-slate-800">Assignment & Location</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Assign To</Label>
                  <Select value={form.assigned_to} onValueChange={v => handleChange('assigned_to', v === 'none' ? '' : v)}>
                    <SelectTrigger className="border-slate-200" data-testid="assigned-to-select"><SelectValue placeholder="Select user" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Unassigned</SelectItem>
                      {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Branch</Label>
                    <Select value={form.branch_id} onValueChange={v => handleChange('branch_id', v === 'none' ? '' : v)}>
                      <SelectTrigger className="border-slate-200" data-testid="branch-select"><SelectValue placeholder="Select branch" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Department</Label>
                    <Input placeholder="e.g. Billing, Claims" value={form.department}
                      onChange={e => handleChange('department', e.target.value)} className="border-slate-200" data-testid="department-input" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white border border-slate-200 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold font-['Outfit'] text-slate-800">Related References</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Related Claim</Label>
                  <Input placeholder="Search claim by number..." value={claimSearch}
                    onChange={e => setClaimSearch(e.target.value)} className="border-slate-200 mb-1" data-testid="claim-search" />
                  {claims.length > 0 && (
                    <Select value={form.related_claim_id} onValueChange={v => handleChange('related_claim_id', v === 'none' ? '' : v)}>
                      <SelectTrigger className="border-slate-200" data-testid="claim-select"><SelectValue placeholder="Select claim" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {claims.map(c => <SelectItem key={c.id} value={c.id}>{c.claim_number} - {c.patient_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                <div>
                  <Label>Related Patient</Label>
                  <Input placeholder="Search patient..." value={patientSearch}
                    onChange={e => setPatientSearch(e.target.value)} className="border-slate-200 mb-1" data-testid="patient-search" />
                  {patients.length > 0 && (
                    <Select value={form.related_patient_id} onValueChange={v => handleChange('related_patient_id', v === 'none' ? '' : v)}>
                      <SelectTrigger className="border-slate-200" data-testid="patient-select"><SelectValue placeholder="Select patient" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {patients.map(p => <SelectItem key={p.id} value={p.id}>{p.name} - {p.uhid}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                <div>
                  <Label>Related Payer/TPA</Label>
                  <Select value={form.related_payer_id} onValueChange={v => handleChange('related_payer_id', v === 'none' ? '' : v)}>
                    <SelectTrigger className="border-slate-200" data-testid="payer-select"><SelectValue placeholder="Select payer" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {payers.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-5">
          <Button type="button" variant="outline" onClick={() => navigate('/tickets')} data-testid="cancel-btn">Cancel</Button>
          <Button type="submit" disabled={loading}
            className="bg-gradient-to-r from-indigo-500 to-violet-600 text-white gap-1.5"
            data-testid="submit-ticket-btn">
            <FloppyDisk className="h-4 w-4" /> {loading ? 'Creating...' : 'Create Ticket'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default NewTicketPage;
